import { isMarketplacePlatform, isYelpPlatform } from '@local/utils';

export const getApiHost = (): string => {
  if (process.env.NODE_ENV === 'test') {
    return 'https://eat.dev.chownow.com/api';
  }

  const apiHost =
    process.env.REACT_APP_API_HOST || process.env.NEXT_PUBLIC_API_HOST;

  if (!apiHost) {
    throw new Error(
      'API host is not configured. Set REACT_APP_API_HOST or NEXT_PUBLIC_API_HOST'
    );
  }

  return apiHost;
};

export const getQueryFetchOptions = (apiVersion?: string): RequestInit => {
  const headers = getHeaders(apiVersion);
  return {
    credentials: 'include',
    method: 'GET',
    headers,
  };
};

type MarketplaceMetadata = {
  marketplace: { id: string; x_id: null };
};

const getMarketplaceMetadata = (): MarketplaceMetadata | null => {
  if (!isMarketplacePlatform()) return null;
  return { marketplace: { id: '2', x_id: null } };
};

export const getMutationFetchOptions = (
  method: 'POST' | 'PUT' | 'DELETE',
  body?: Record<string, unknown>,
  apiVersion?: string
): RequestInit => {
  const metadata = getMarketplaceMetadata();
  const bodyWithMetadata = body
    ? { ...body, ...(metadata ? { meta: metadata } : {}) }
    : undefined;
  return {
    credentials: 'include',
    method,
    headers: getHeaders(apiVersion),
    ...(bodyWithMetadata !== undefined
      ? { body: JSON.stringify(bodyWithMetadata) }
      : {}),
  };
};

const getHeaders = (apiVersion?: string): HeadersInit => {
  const version = apiVersion || '3.0';
  const reactPackage = getPackage();
  return {
    Accept: `application/json version=${version};`,
    'Content-Type': 'application/json; charset=UTF-8',
    'X-CN-App-Info': `${reactPackage}/${
      process.env.REACT_APP_VERSION || process.env.VERSION
    }`,
  };
};

const getPackage = (): string | undefined => {
  if (isMarketplacePlatform()) {
    return 'Marketplace-Web';
  } else if (isYelpPlatform()) {
    return 'Yelp-Web';
  }

  return process.env.REACT_APP_PACKAGE;
};

export const parseHttpResponse = <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Not Authorized');
    }
    throw new Error('Error fetching resource');
  }
  return response.json();
};

export const parseMutationHttpResponse = async <T, TErrorItem = unknown>(
  response: Response
): Promise<T> => {
  if (response.ok) {
    return await response.json() as T;
  }

  const data = await response.json().catch(() => null);
  const fallbackErrors = [
    {
      code: String(response.status),
      message: 'Error fetching resource',
      reason: 'unknown_error',
    } as TErrorItem,
  ];

  switch (response.status) {
    case 400:
    case 403:
      throw new MutationHttpError<TErrorItem>(
        (data?.errors as TErrorItem[]) ?? fallbackErrors
      );
    default:
      throw new MutationHttpError<TErrorItem>(fallbackErrors);
  }
};

export class MutationHttpError<TErrorItem = unknown> extends Error {
  errors: TErrorItem[];

  constructor(errors: TErrorItem[], message = 'Error fetching resource') {
    super(message);
    this.name = 'MutationHttpError';
    this.errors = errors;
  }
}

/**
 * returns an abort signal that includes a timeout, if supported by the browser
 * for older browsers where not supported, returns the original abort signal without timeout
 * @param signal
 * @param timeout
 */
export const getAbortSignal = (signal: AbortSignal, timeout: number) => {
  return {
    signal: AbortSignal.any
      ? AbortSignal.any([signal, AbortSignal.timeout(timeout)])
      : signal,
  };
};
